import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { motion } from 'motion/react'
import { useNotify } from '../components/notice/useNotify'
import {
  AdminUser,
  AdminUserSearchResult,
  getAdminUser,
  resetUserUsage,
  searchUsers,
  setUserLogFullLlm,
} from '../api/client'

const fmt = (n: number) => n.toLocaleString()

const userLabel = (u: { id: number; name: string | null; email: string | null }) =>
  u.name || u.email || `User ${u.id}`

const errText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || fallback

// A used-vs-cap meter for one rolling window (minute or day).
const UsageBar: React.FC<{ label: string; used: number; cap: number }> = ({ label, used, cap }) => {
  const pct = cap > 0 ? Math.min(100, (used / cap) * 100) : 0
  const over = used >= cap
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-sm text-gray-600">{label}</span>
        <span className="text-sm tabular-nums text-gray-800">
          {fmt(used)} <span className="text-gray-400">/ {fmt(cap)}</span>
        </span>
      </div>
      <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
        <div
          className={`h-full rounded-full ${over ? 'bg-danger' : 'bg-action'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

const AdminUserPage: React.FC = () => {
  const { userId } = useParams<{ userId?: string }>()
  const navigate = useNavigate()

  const [query, setQuery] = useState('')
  const [results, setResults] = useState<AdminUserSearchResult[]>([])
  const [searching, setSearching] = useState(false)

  const [user, setUser] = useState<AdminUser | null>(null)
  const [loading, setLoading] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [savingLogFull, setSavingLogFull] = useState(false)
  // Load and search failures stay inline: they stand in for the content
  // (and gate "User not found"). The toggle's and the reset's outcomes are
  // one-shot, so they go on the app-wide notice.
  const [error, setError] = useState<string | null>(null)
  const notify = useNotify()

  const loadUser = useCallback(async (id: number) => {
    setLoading(true)
    setError(null)
    try {
      setUser(await getAdminUser(id))
    } catch (e) {
      setUser(null)
      setError(errText(e, 'Failed to load user.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (userId) {
      void loadUser(Number(userId))
    } else {
      setUser(null)
    }
  }, [userId, loadUser])

  const runSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    const q = query.trim()
    if (!q) return
    setSearching(true)
    setError(null)
    try {
      setResults(await searchUsers(q))
    } catch (err) {
      setError(errText(err, 'Search failed.'))
    } finally {
      setSearching(false)
    }
  }

  const onToggleLogFull = async () => {
    if (!user) return
    const next = !user.log_full_llm
    setSavingLogFull(true)
    try {
      await setUserLogFullLlm(user.id, next)
      setUser({ ...user, log_full_llm: next })
      notify.success(
        next
          ? 'Full LLM logging ON — this user’s prompts, tool results, and tool-call args are now stored un-clipped.'
          : 'Full LLM logging OFF — back to clipped logging for this user.',
      )
    } catch (e) {
      notify.error(errText(e, 'Failed to update logging flag.'))
    } finally {
      setSavingLogFull(false)
    }
  }

  const onReset = async () => {
    if (!user) return
    setResetting(true)
    try {
      const { deleted } = await resetUserUsage(user.id)
      notify.success(`Cleared usage — removed ${fmt(deleted)} recorded call${deleted === 1 ? '' : 's'}.`)
      await loadUser(user.id)
    } catch (e) {
      notify.error(errText(e, 'Reset failed.'))
    } finally {
      setResetting(false)
    }
  }

  return (
    <div className="flex flex-col min-h-screen bg-blush-light">
      <div className="container mx-auto px-4 pb-8 sm:pt-8 max-w-3xl">
        <div className="mb-6">
          <Link to="/admin/llm-usage" className="text-sm text-amber-800 hover:underline">
            ← LLM Usage
          </Link>
          <h1 className="text-3xl font-bold text-gray-800 mt-1">Manage user</h1>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3">
            {error}
          </div>
        )}

        {/* Search */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <form onSubmit={runSearch} className="flex items-center gap-2">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, email, or user ID"
              className="flex-1 min-w-0 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-action"
            />
            <motion.button
              type="submit"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              disabled={searching}
              className="shrink-0 whitespace-nowrap rounded-md bg-action text-plum px-4 py-2 text-sm disabled:opacity-50"
            >
              {searching ? 'Searching…' : 'Search'}
            </motion.button>
          </form>

          {results.length > 0 && (
            <ul className="mt-4 divide-y divide-gray-100">
              {results.map((r) => (
                <li key={r.id}>
                  <button
                    onClick={() => navigate(`/admin/users/${r.id}`)}
                    className="w-full text-left px-2 py-2 rounded hover:bg-gray-50 flex flex-col sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                  >
                    {/* Stacked on phones, name-left / email-right from sm up;
                        truncated either way, so a long name or email cannot
                        widen the page. The full value is in the tooltip. */}
                    <span className="min-w-0 truncate text-gray-800" title={userLabel(r)}>
                      {userLabel(r)}
                    </span>
                    <span
                      className="min-w-0 truncate text-sm text-gray-400"
                      title={`${r.email ? `${r.email} · ` : ''}#${r.id}`}
                    >
                      {r.email ? `${r.email} · ` : ''}#{r.id}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Detail */}
        {loading && <div className="text-gray-500">Loading…</div>}

        {user && !loading && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">
                  {userLabel(user)}
                  {user.is_admin && (
                    <span className="ml-2 align-middle text-xs font-medium rounded-full bg-amber-100 text-amber-800 px-2 py-0.5">
                      admin
                    </span>
                  )}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  #{user.id}
                  {user.email ? ` · ${user.email}` : ''}
                  {user.created_at
                    ? ` · joined ${new Date(user.created_at).toLocaleDateString()}`
                    : ''}
                </p>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <UsageBar
                label="Per-minute usage"
                used={user.usage.minute_used}
                cap={user.usage.minute_cap}
              />
              <UsageBar
                label="Daily usage"
                used={user.usage.day_used}
                cap={user.usage.day_cap}
              />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-md border border-gray-200 px-4 py-3 mb-6">
              <div>
                <div className="text-sm font-medium text-gray-800">Full LLM logging</div>
                <p className="text-sm text-gray-400 mt-0.5">
                  Store this user’s prompts, tool results, and tool-call args
                  un-clipped in the LLM logs. Rows get large — use for debugging,
                  then turn off.
                </p>
              </div>
              <motion.button
                onClick={onToggleLogFull}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                disabled={savingLogFull}
                role="switch"
                aria-checked={user.log_full_llm}
                aria-label="Full LLM logging"
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                  user.log_full_llm ? 'bg-action' : 'bg-gray-300'
                }`}
              >
                <motion.span
                  layout
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  className={`inline-block h-5 w-5 rounded-full bg-white shadow ${
                    user.log_full_llm ? 'ml-auto mr-0.5' : 'ml-0.5'
                  }`}
                />
              </motion.button>
            </div>

            <div className="flex items-center gap-3">
              <motion.button
                onClick={onReset}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                disabled={resetting}
                className="rounded-md bg-action text-plum px-4 py-2 text-sm disabled:opacity-50"
              >
                {resetting ? 'Clearing…' : 'Clear usage'}
              </motion.button>
              <span className="text-sm text-gray-400">
                Removes their recorded calls from the last 24h so both windows reset to zero.
              </span>
            </div>
          </div>
        )}

        {!user && !loading && userId && !error && (
          <div className="text-gray-500">User not found.</div>
        )}
      </div>
    </div>
  )
}

export default AdminUserPage
